/**
 * @file index.tsx
 *
 * @fileoverview A component that places the children in the page frame. With a max width.
 */

import { cva } from "class-variance-authority";
import { ReactNode } from "react";
import { ColorsProps, generateColorClasses } from "../../../../theme/colors";
import {
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../../theme/spacing";
import styles from "./main.module.scss";

const main = cva([styles.main], {
  variants: {
    size: {
      unset: null,
      small: styles.mainSmall,
      medium: styles.mainMedium,
      large: styles.mainLarge,
      xLarge: styles.mainXLarge,
    },
  },
});

type BaseMainProps = SpaceProps & ColorsProps;

export interface MainProps extends BaseMainProps {
  /**
   * Text for the button
   *
   * @default null
   */
  children?: ReactNode;
  /**
   * How large the container can be as defined in the theme object.
   *
   * @default medium
   */
  size?: "small" | "medium" | "large" | "xLarge" | "unset";
}

/**
 * Renders content with a max width on the center of the page. Useful for placing
 * main content on a normal page.
 */
export default function Main({
  size = "large",
  children,
  ...props
}: MainProps) {
  return (
    <div
      className={`
            ${main({ size })}
            ${generateMarginClasses(props, styles)}
            ${generatePaddingClasses(props, styles)}
            ${generateColorClasses(props, styles)}
            `.trim()}
    >
      {children}
    </div>
  );
}

export { Main };
